import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import ChevronLeftOutlinedIcon from "@mui/icons-material/ChevronLeftOutlined";
import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined";
import ZoomInOutlinedIcon from "@mui/icons-material/ZoomInOutlined";
import ZoomOutOutlinedIcon from "@mui/icons-material/ZoomOutOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { getFileContent } from "../../../../api/Design/fileApi";
import {
  getMuestraFileKey,
  isMuestraVideoFile,
} from "../../../../utils/muestraFotosExport";

const imageCache = new Map();
const MIN_ZOOM = 1;
const MAX_ZOOM = 10;
const ZOOM_STEP = 0.35;

async function loadVideoObjectUrl(muestraId, file) {
  const token = localStorage.getItem("token");
  const response = await axios.get(
    `${process.env.REACT_APP_API_URL}/api/files/muestra/${muestraId}/download/${encodeURIComponent(file.name)}`,
    {
      headers: { Authorization: `Bearer ${token}` },
      params: file.category ? { category: file.category } : undefined,
      responseType: "blob",
    },
  );
  const mime =
    file.mimeType || response.headers?.["content-type"] || "video/mp4";
  const blob =
    response.data instanceof Blob
      ? response.data.type
        ? response.data
        : new Blob([response.data], { type: mime })
      : new Blob([response.data], { type: mime });
  return URL.createObjectURL(blob);
}

async function loadImageDataUrl(file) {
  const key = getMuestraFileKey(file);
  if (imageCache.has(key)) return imageCache.get(key);
  const response = await getFileContent(file.path || key);
  const data = response?.data;
  if (!data?.data || !data?.mimeType) {
    throw new Error("empty");
  }
  const url = `data:${data.mimeType};base64,${data.data}`;
  imageCache.set(key, url);
  return url;
}

function clampZoom(value) {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));
}

export default function MuestraPhotosCarouselDialog({
  open,
  files = [],
  index = 0,
  onIndexChange,
  onClose,
  muestraId,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const videoUrlRef = useRef(null);
  const stageRef = useRef(null);
  const viewRef = useRef({ zoom: 1, offset: { x: 0, y: 0 } });
  const dragRef = useRef(null);
  const pinchRef = useRef(null);
  const safeIndex = Math.min(Math.max(index, 0), Math.max(files.length - 1, 0));
  const file = files[safeIndex] || null;
  const fileKey = file ? getMuestraFileKey(file) : "";
  const isVideo = Boolean(file && isMuestraVideoFile(file));
  const canPrev = safeIndex > 0;
  const canNext = safeIndex < files.length - 1;
  const isZoomed = zoom > 1.01;

  viewRef.current = { zoom, offset };

  const resetView = useCallback(() => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    setDragging(false);
    dragRef.current = null;
    pinchRef.current = null;
  }, []);

  useEffect(() => {
    resetView();
  }, [fileKey, open, resetView]);

  useEffect(() => {
    if (!open || !file) return undefined;
    let cancelled = false;

    const revokeVideo = () => {
      if (videoUrlRef.current) {
        URL.revokeObjectURL(videoUrlRef.current);
        videoUrlRef.current = null;
      }
    };

    setError(null);
    if (!isVideo && imageCache.has(fileKey)) {
      revokeVideo();
      setSrc(imageCache.get(fileKey));
      setLoading(false);
    } else {
      setSrc(null);
      setLoading(true);
      revokeVideo();
    }

    const load = async () => {
      try {
        const url = isVideo
          ? await loadVideoObjectUrl(muestraId, file)
          : await loadImageDataUrl(file);
        if (cancelled) {
          if (isVideo) URL.revokeObjectURL(url);
          return;
        }
        if (isVideo) videoUrlRef.current = url;
        setSrc(url);
        setLoading(false);
      } catch (err) {
        console.error("Error abriendo foto de muestra:", err);
        if (!cancelled) {
          setError(
            t("Samples.videoOpenError", {
              defaultValue: "No se pudo abrir el archivo",
            }),
          );
          setLoading(false);
        }
      }
    };

    if (isVideo || !imageCache.has(fileKey)) load();

    return () => {
      cancelled = true;
    };
  }, [open, file, fileKey, isVideo, muestraId, t]);

  useEffect(() => {
    if (!open) return undefined;
    const next = files[safeIndex + 1];
    if (!next || isMuestraVideoFile(next)) return undefined;
    const key = getMuestraFileKey(next);
    if (imageCache.has(key)) return undefined;
    let cancelled = false;
    loadImageDataUrl(next).catch(() => {
      if (!cancelled) {
        /* la siguiente foto se reintentará al llegar */
      }
    });
    return () => {
      cancelled = true;
    };
  }, [open, files, safeIndex]);

  useEffect(() => {
    return () => {
      if (videoUrlRef.current) {
        URL.revokeObjectURL(videoUrlRef.current);
        videoUrlRef.current = null;
      }
    };
  }, []);

  const applyZoomAtPoint = useCallback((nextZoom, clientX, clientY) => {
    const stage = stageRef.current;
    const { zoom: prevZoom, offset: prevOffset } = viewRef.current;
    const zoomed = clampZoom(nextZoom);
    if (zoomed <= MIN_ZOOM) {
      setZoom(MIN_ZOOM);
      setOffset({ x: 0, y: 0 });
      return;
    }
    if (!stage) {
      setZoom(zoomed);
      return;
    }
    const rect = stage.getBoundingClientRect();
    const pointX = clientX - rect.left - rect.width / 2;
    const pointY = clientY - rect.top - rect.height / 2;
    const ratio = zoomed / prevZoom;
    setZoom(zoomed);
    setOffset({
      x: pointX - (pointX - prevOffset.x) * ratio,
      y: pointY - (pointY - prevOffset.y) * ratio,
    });
  }, []);

  const bumpZoom = useCallback(
    (delta) => {
      const stage = stageRef.current;
      const { zoom: current } = viewRef.current;
      if (!stage) {
        const next = clampZoom(current + delta);
        setZoom(next);
        if (next <= MIN_ZOOM) setOffset({ x: 0, y: 0 });
        return;
      }
      const rect = stage.getBoundingClientRect();
      applyZoomAtPoint(
        current + delta,
        rect.left + rect.width / 2,
        rect.top + rect.height / 2,
      );
    },
    [applyZoomAtPoint],
  );

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      const tag = String(event.target?.tagName || "").toLowerCase();
      if (
        tag === "input" ||
        tag === "textarea" ||
        event.target?.isContentEditable
      ) {
        return;
      }
      if (event.key === "+" || event.key === "=") {
        event.preventDefault();
        bumpZoom(ZOOM_STEP);
        return;
      }
      if (event.key === "-" || event.key === "_") {
        event.preventDefault();
        bumpZoom(-ZOOM_STEP);
        return;
      }
      if (event.key === "0") {
        event.preventDefault();
        resetView();
        return;
      }
      if (event.key === "ArrowLeft" && canPrev && !isZoomed) {
        event.preventDefault();
        onIndexChange?.(safeIndex - 1);
      } else if (event.key === "ArrowRight" && canNext && !isZoomed) {
        event.preventDefault();
        onIndexChange?.(safeIndex + 1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    open,
    canPrev,
    canNext,
    onIndexChange,
    safeIndex,
    isZoomed,
    bumpZoom,
    resetView,
  ]);

  useEffect(() => {
    const stage = stageRef.current;
    if (!open || !stage || isVideo) return undefined;
    const onWheel = (event) => {
      event.preventDefault();
      const direction = event.deltaY < 0 ? 1 : -1;
      const { zoom: current } = viewRef.current;
      applyZoomAtPoint(
        current + direction * ZOOM_STEP,
        event.clientX,
        event.clientY,
      );
    };
    stage.addEventListener("wheel", onWheel, { passive: false });
    return () => stage.removeEventListener("wheel", onWheel);
  }, [open, isVideo, applyZoomAtPoint, src]);

  const handleDoubleClick = (event) => {
    if (isVideo || !src) return;
    event.preventDefault();
    if (isZoomed) {
      resetView();
      return;
    }
    applyZoomAtPoint(2.5, event.clientX, event.clientY);
  };

  const handlePointerDown = (event) => {
    if (isVideo || !src || zoom <= 1.01 || event.button !== 0) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: offset.x,
      originY: offset.y,
    };
    setDragging(true);
  };

  const handlePointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    setOffset({
      x: drag.originX + (event.clientX - drag.startX),
      y: drag.originY + (event.clientY - drag.startY),
    });
  };

  const handlePointerUp = (event) => {
    if (dragRef.current?.pointerId === event.pointerId) {
      dragRef.current = null;
      setDragging(false);
    }
  };

  const touchDistance = (touches) => {
    const [a, b] = touches;
    const dx = a.clientX - b.clientX;
    const dy = a.clientY - b.clientY;
    return Math.hypot(dx, dy);
  };

  const touchCenter = (touches) => {
    const [a, b] = touches;
    return {
      x: (a.clientX + b.clientX) / 2,
      y: (a.clientY + b.clientY) / 2,
    };
  };

  const handleTouchStart = (event) => {
    if (isVideo || !src || event.touches.length !== 2) return;
    event.preventDefault();
    dragRef.current = null;
    setDragging(false);
    const center = touchCenter(event.touches);
    pinchRef.current = {
      startDist: touchDistance(event.touches),
      startZoom: viewRef.current.zoom,
      centerX: center.x,
      centerY: center.y,
    };
  };

  const handleTouchMove = (event) => {
    const pinch = pinchRef.current;
    if (!pinch || event.touches.length !== 2) return;
    event.preventDefault();
    const dist = touchDistance(event.touches);
    if (!pinch.startDist) return;
    const ratio = dist / pinch.startDist;
    applyZoomAtPoint(pinch.startZoom * ratio, pinch.centerX, pinch.centerY);
  };

  const handleTouchEnd = () => {
    pinchRef.current = null;
  };

  const fileName = file?.name || fileKey.split("/").pop() || "";
  const zoomPercent = Math.round(zoom * 100);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          bgcolor: "rgba(12,12,12,0.97)",
          backgroundImage: "none",
          display: "flex",
          flexDirection: "column",
          height: { xs: "100dvh", sm: "min(88vh, 920px)" },
          m: { xs: 0, sm: 2 },
          maxHeight: { xs: "100dvh", sm: "min(88vh, 920px)" },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: 1.5,
          py: 1,
          color: "common.white",
        }}
      >
        <Typography
          variant="subtitle2"
          noWrap
          title={fileName}
          sx={{ fontWeight: 700, minWidth: 0 }}
        >
          {fileName}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          {!isVideo && src ? (
            <>
              <Tooltip title={t("Samples.photoViewerZoomOut")}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => bumpZoom(-ZOOM_STEP)}
                    disabled={zoom <= MIN_ZOOM}
                    sx={{ color: "common.white" }}
                  >
                    <ZoomOutOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Typography
                variant="caption"
                sx={{
                  minWidth: 44,
                  textAlign: "center",
                  fontWeight: 800,
                  opacity: 0.9,
                }}
              >
                {zoomPercent}%
              </Typography>
              <Tooltip title={t("Samples.photoViewerZoomIn")}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => bumpZoom(ZOOM_STEP)}
                    disabled={zoom >= MAX_ZOOM}
                    sx={{ color: "common.white" }}
                  >
                    <ZoomInOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t("Samples.photoViewerZoomReset")}>
                <span>
                  <IconButton
                    size="small"
                    onClick={resetView}
                    disabled={!isZoomed}
                    sx={{ color: "common.white" }}
                  >
                    <RestartAltOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          ) : null}
          {files.length > 0 ? (
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, opacity: 0.85, ml: 0.5 }}
            >
              {t("Samples.photoViewerCounter", {
                current: safeIndex + 1,
                total: files.length,
              })}
            </Typography>
          ) : null}
          <IconButton
            aria-label={t("common.close", { defaultValue: "Cerrar" })}
            onClick={onClose}
            sx={{ color: "common.white" }}
          >
            <CloseOutlinedIcon />
          </IconButton>
        </Box>
      </Box>

      <Box
        ref={stageRef}
        onDoubleClick={handleDoubleClick}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        sx={{
          position: "relative",
          flex: 1,
          minHeight: 0,
          mx: { xs: 0, sm: 1.5 },
          mb: { xs: 0, sm: 1.5 },
          borderRadius: { xs: 0, sm: 1.5 },
          bgcolor: isVideo
            ? "common.black"
            : alpha(theme.palette.common.white, 0.06),
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          touchAction: isVideo ? "auto" : "none",
          cursor: isVideo
            ? "default"
            : isZoomed
              ? dragging
                ? "grabbing"
                : "grab"
              : "zoom-in",
          userSelect: "none",
        }}
      >
        {loading ? (
          <CircularProgress size={32} sx={{ color: "common.white" }} />
        ) : null}
        {error ? (
          <Typography color="error" sx={{ px: 2, textAlign: "center" }}>
            {error}
          </Typography>
        ) : null}
        {!loading && !error && src && isVideo ? (
          <Box
            component="video"
            key={fileKey}
            src={src}
            controls
            autoPlay
            playsInline
            sx={{
              maxWidth: "100%",
              maxHeight: "100%",
              width: "auto",
              height: "auto",
              objectFit: "contain",
              bgcolor: "black",
              display: "block",
            }}
          />
        ) : null}
        {!loading && !error && src && !isVideo ? (
          <Box
            component="img"
            key={fileKey}
            src={src}
            alt={fileName}
            draggable={false}
            sx={{
              maxWidth: "100%",
              maxHeight: "100%",
              width: "auto",
              height: "auto",
              objectFit: "contain",
              objectPosition: "center",
              display: "block",
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
              transformOrigin: "center center",
              transition: dragging ? "none" : "transform 80ms ease-out",
              willChange: "transform",
              pointerEvents: "none",
            }}
          />
        ) : null}

        {!isVideo && !loading && !error && src ? (
          <Typography
            variant="caption"
            sx={{
              position: "absolute",
              left: 12,
              bottom: 10,
              px: 1,
              py: 0.35,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.common.black, 0.45),
              color: "common.white",
              fontWeight: 600,
              pointerEvents: "none",
            }}
          >
            {t("Samples.photoViewerZoomHint")}
          </Typography>
        ) : null}

        {files.length > 1 ? (
          <>
            <IconButton
              aria-label={t("Meetings.mediaPreviewPrev")}
              onClick={() => onIndexChange?.(safeIndex - 1)}
              disabled={!canPrev}
              sx={{
                position: "absolute",
                left: 8,
                top: "50%",
                transform: "translateY(-50%)",
                bgcolor: alpha(theme.palette.common.black, 0.45),
                color: "common.white",
                zIndex: 2,
                "&:hover": { bgcolor: alpha(theme.palette.common.black, 0.65) },
                "&.Mui-disabled": {
                  bgcolor: alpha(theme.palette.common.black, 0.2),
                  color: alpha(theme.palette.common.white, 0.35),
                },
              }}
            >
              <ChevronLeftOutlinedIcon />
            </IconButton>
            <IconButton
              aria-label={t("Meetings.mediaPreviewNext")}
              onClick={() => onIndexChange?.(safeIndex + 1)}
              disabled={!canNext}
              sx={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                bgcolor: alpha(theme.palette.common.black, 0.45),
                color: "common.white",
                zIndex: 2,
                "&:hover": { bgcolor: alpha(theme.palette.common.black, 0.65) },
                "&.Mui-disabled": {
                  bgcolor: alpha(theme.palette.common.black, 0.2),
                  color: alpha(theme.palette.common.white, 0.35),
                },
              }}
            >
              <ChevronRightOutlinedIcon />
            </IconButton>
          </>
        ) : null}
      </Box>
    </Dialog>
  );
}
